import App from 'app/app.js';
import 'app/store/JobStore.js';
import 'app/store/UserProfileStore.js';
import 'app/service/StorageService.js';
import 'app/store/ShoppingCartStore.js';
import 'app/store/ContentStore.js';
import 'app/action/DeserializeAction.js';
import 'app/action/SessionRecreatedAction.js';
import { sanitizeAllInputs } from '@app/utils/sanitize';

const afterCheckoutPaths = ['/order-receipt', '/review-order', '/billing-info', '/taxInfo', '/payment-info'];

function RouterService(
  JobStore,
  UserProfileStore,
  ShoppingCartStore,
  ContentStore,
  StorageService,
  Config,
  $route,
  $location,
  $injector,
  $rootScope,
  $document,
  $window,
  DeserializeAction,
  SessionRecreatedAction,
  ProductCatalogStore,
  IdentityService,
  SessionTimeoutService,
  ErrorLogService
) {
  const SKU_SET_REGEX = /\/skuSet\/(?<setName>[^\/]+)$/;

  var history = [],
    unregister,
    source;

  this.redirectedSmartUrlRoute = null;
  this.smartUrlTriggered = false;
  this.deepLinkTriggered = false;

  this.updateSession = function () {
    var http = $injector.get('$http');
    // Will make sure to update session on server whenever user changes pages
    // if session has timed out on the server, error will be handled by SessionTimeoutInterceptor
    http.post('keepAlive', { path: $location.path() });
  };

  // controllers need to register their paths here
  this.paths = {};

  if (!Config.testMode) {
    // Register a listener for $locationChangeStart event which prevents all location changes.
    // This listener will be replaced with the real one when the content is loaded.
    unregister = $rootScope.$on('$locationChangeStart', function (event) {
      event.preventDefault();
    });
  }

  // This initialization method should be invoked after the content is loaded.
  this.init = function () {
    if (!!unregister) {
      unregister();
    }

    source = $location.search().source;

    $rootScope.$on(
      '$locationChangeStart',
      function (event, next) {
        if (this.intercept(next)) {
          event.preventDefault();
        }
      }.bind(this)
    );

    $rootScope.$on(
      '$locationChangeSuccess',
      function (event, current, previous) {
        if (R.eqDeep(history, []) && !!previous) {
          let url = '/';
          if (previous.includes('#')) {
            url = R.last(previous.split('#'));
          }

          if (this.isSmartUrl(url)) {
            history.push(url);
          } else {
            history.push(url.split('?')[0]);
          }
        }

        if (history.slice(-2)[0] === $location.path()) {
          // back button was hit. remove last 2 items from array

          history.pop();
        } else {
          var path = $location.path();

          history.push(this.isSmartUrl(path) ? $location.url() : path);
        }

        StorageService.setData('path', $location.path());
        this.updateStorage();
        if (!SessionTimeoutService.isSessionTimeoutRedirect()) {
          this.updateSession();
        }
      }.bind(this)
    );

    if (
      !$location.path() || // home page
      !this.intercept($location.path())
    ) {
      // Reload the $route to trigger route change which was being prevented till now
      $injector.get('$route').reload();
    }
  };

  this.getRoute = function (path) {
    // NOTE: Unable to inject $route directly do to the phenomenon documented here: https://github.com/angular/angular.js/issues/2717
    var $route = $injector.get('$route');

    // Need an array of all registered routes to generate comparable regexps
    // Note that the list is filtered by "/" in order to ignore default routes "" and null
    // Note that the list is ordered descending so that the home path (i.e. "/") is found last
    var keys = R.reverse(R.sortBy(R.identity, R.filter(R.test(/^\//), R.keys($route.routes))));

    // Creating a collection of routes with a corresponding regexp that can be used
    // in an attempt to match the current location
    var map = R.map(function (key) {
      return { key: key, regexp: new RegExp('^' + key.replace(/:\w+/g, '[^\/?]+')) };
    }, keys);

    // Find the route key which matches the location
    var match = R.find(function (e) {
      return R.test(e.regexp, path);
    })(map)?.key;

    // Return the matched route configuration
    return $route.routes[match];
  };

  this.intercept = function (path) {
    const route = this.getRoute($location.path());

    if (!route) {
      return false;
    }

    const jobRequired = route.jobRequired !== false; // default value for undefined is true
    const loginRequired = !!route.loginRequired; // default value for undefined is false
    const elevationRequired = !!route.elevationRequired; // default value for undefined is false
    const orderRequired = route.orderRequired !== false; // default value for undefined is true
    const orderFlow = route.orderFlow !== false; // default value for undefined is true
    const packageRequired = !!route.packageRequired; // default value for undefined is false
    const cyopRequired = !!route.cyopRequired; // default value for undefined is false
    const addOnRequired = !!route.addOnRequired; // default value for undefined is false
    const bundleRequired = !!route.bundleRequired; // default value for undefined is false

    //MLC-12113 - redirect to school finder
    if ($location.path() === '/findmypictureday') {
      $rootScope.$evalAsync(() => this.goToFindSchool());
      return true;
    }

    // if job is required and job is not loaded, redirect to homepage
    if (jobRequired && !JobStore.isJobLoaded()) {
      $rootScope.$evalAsync(() => this.goToHome());
      return true;
    }

    // if login is required and user is not logged in, redirect to login page
    if (loginRequired && !UserProfileStore.isLoggedIn()) {
      $rootScope.$evalAsync(() => this.goToLogin());
      return true;
    }

    const isSmartUrl = this.isSmartUrl($location.path());

    if (isSmartUrl) {
      this.redirectedSmartUrlRoute = {
        path: $location.path(),
        search: sanitizeAllInputs($location.search()),
        routeParams: sanitizeAllInputs(this.parseSmartUrlRouteParams($location.path()))
      };
    }

    // if user is logged in via Identity and requires elevation
    // tip for easy debugging and testing: run `$sflyAuth.authTime = 1` in console to require elevation
    if (elevationRequired && UserProfileStore.isUserLoggedInWithIdentity() && IdentityService.isElevationRequired()) {
      $rootScope.$evalAsync(() => IdentityService.goToElevation(isSmartUrl ? $location.absUrl() : null));
      return true;
    }

    if (UserProfileStore.isAuthenticated()) {
      if (isSmartUrl && $location.path().includes('/saved-cart') && ShoppingCartStore.isRestoredItemsAvailable()) {
        $rootScope.$evalAsync(() => this.goToShoppingCart());
        return true;
      } else if ($location.path() === '/' || isSmartUrl) {
        $rootScope.$evalAsync(() => this.goToMyDashboard());
        return true;
      }
    }

    if (
      afterCheckoutPaths.some((p) => $location.path().includes(p)) &&
      ShoppingCartStore.getPreorderSubjects().length > 0
    ) {
      $rootScope.$evalAsync(() => this.goToShoppingCart());
      return true;
    }

    if (orderRequired && !ShoppingCartStore.isOrderOpen()) {
      $rootScope.$evalAsync(() => this.goToHome());
      return true;
    }

    if (orderFlow && JobStore.isPayOnly() && JobStore.isEnhancedPayOnly()) {
      $rootScope.$evalAsync(() => this.goToEnhancedPayOnly());
      return true;
    }

    if (orderFlow && JobStore.isPayOnly() && !JobStore.isEnhancedPayOnly()) {
      $rootScope.$evalAsync(() => this.goToPayOnly());
      return true;
    }

    // if item in price program is required but not available, redirect to portrait store
    if (
      (packageRequired && !ProductCatalogStore.containsPackage()) ||
      (cyopRequired && !ProductCatalogStore.containsCyop()) ||
      (addOnRequired && !ProductCatalogStore.containsAddOn()) ||
      (bundleRequired && !ProductCatalogStore.containsBundle())
    ) {
      $rootScope.$evalAsync(() => this.goToHome());
      return true;
    }

    return false;
  };

  this.goTo = function (path) {
    return function () {
      if ($location.path() === path) {
        $route.reload(); // Refreshing the same page
      }
      $location.url(path);
    }.bind(this);
  };

  this.goToWindow = function (path, anchor) {
    return function () {
      if (anchor) {
        $window.open(path + anchor);
      } else {
        $window.open(path);
      }
    }.bind(this);
  };

  this.goBack = function () {
    var prevUrl = history.length > 1 ? history.splice(-2, 2)[0] : '/';
    $location.path(prevUrl);
    this.updateStorage();
  };

  this.goBackV2 = function () {
    var prevUrl = history.length > 1 ? history.splice(-2, 2)[0] : '/';
    $location.url(prevUrl);
  };

  this.updateStorage = function () {
    StorageService.setData('router', {
      history: history
    });
  };

  this.onDeserialized = function (type, data) {
    if (!!data && !!data.router) {
      history = data.router.history;
    }
  };

  this.getHistoryString = function () {
    return JSON.stringify(history);
  };

  this.pushLocationToHistory = function (path) {
    history.push(path);
    this.updateStorage();
  };

  this.isSflyMobile = function () {
    return source === 'SFLY_MOBILE';
  };

  this.goToAddOn = function () {
    $location.path('/addon');
  };

  this.goToPackages = function () {
    $location.path('/packages');
  };

  this.getSource = function () {
    return source;
  };

  this.showIdentityNavigation = function () {
    var path = $location.path() || '';
    //list of paths here: https://lifetouchlnss.atlassian.net/wiki/spaces/MYC/pages/874741903/Identity+Page+URLS
    var identityPaths = [
      'login',
      'enter-password',
      'password-recover',
      'link-sent',
      'reset-password',
      'switch-account',
      'create-account',
      'loyalty-enrollment',
      'update-profile',
      'sign-in-again',
      'welcome-back'
    ];
    var showIdentity = false;
    for (var i = 0, len = identityPaths.length; i < len; i++) {
      if (path.indexOf(identityPaths[i]) > -1) {
        showIdentity = true;
      }
    }
    return showIdentity;
  };

  this.showExpandableBanner = function () {
    var path = $location.path() || '';
    return path.indexOf('portrait-store') > -1;
  };

  this.getSPAPath = function () {
    return $location.absUrl().split('#')[0];
  };

  this.getAbsUrl = function () {
    return $location.absUrl();
  };

  /**
   * Find the last index of login in the history, if its index is greater than or equal to one, navigate to the
   * page before it. This was done to return the user directly into a flow instead of going to the dashboard.
   * If there is no history or no /login and this is called it will redirect to the homepage.
   */
  this.getPathBeforeLogin = function () {
    if (history.length > 1 && history.lastIndexOf('/login') >= 1) {
      return history[history.lastIndexOf('/login') - 1];
    }
  };

  this.redirectAfterAccountCreation = function () {
    var pathBeforeLogin = this.getPathBeforeLogin();
    var accountCreateFromPopup = StorageService.get('createAccountFromPopup');
    StorageService.remove('createAccountFromPopup');
    if (
      accountCreateFromPopup ||
      pathBeforeLogin === this.paths.SHOPPING_CART ||
      pathBeforeLogin === this.paths.BILLING_INFO ||
      pathBeforeLogin === this.paths.BILLING_INFO_WITH_MESSAGE
    ) {
      return this.goBackBeforeLogin();
    }

    this.goToMyDashboard();
  };

  this.goBackBeforeLogin = function () {
    var newPath = this.getPathBeforeLogin();
    if (!!newPath) {
      const [path, query] = newPath.split('?');
      $location.path(path)?.search(query);
      this.updateStorage();
    } else {
      $location.path('/');
      this.updateStorage();
    }
  };

  this.pushLocationIfNotLastVisited = function (path) {
    if (history.length === 0) {
      this.pushLocationToHistory(this.isSmartUrl($location.path()) ? $location.url() : this.paths.HOME);
    }
    var lastIndex = history.lastIndexOf(path);
    if (!(lastIndex > -1 && lastIndex === history.length - 1)) {
      this.pushLocationToHistory(path);
    }
  };

  this.parseSmartUrlRouteParams = function (smartUrl) {
    const parsers = {
      portraitIdParams: /^\/accessCredentials\/(?<identifier>[^\/]+)\/(?<accessCode>[^\/]+)/,
      pictureDayIdParams: /^\/pictureDayId\/(?<identifier>[^\/]+)/,
      deepLinkParams: /\/(package|addon)\/(?<sku>\d+)$/,
      skuSetParams: SKU_SET_REGEX
    };
    return Object.values(parsers).reduce((routeParams, parser) => {
      Object.assign(routeParams, smartUrl.match(parser)?.groups);
      return routeParams;
    }, {});
  };

  this.isSmartUrl = function (path) {
    return (
      Boolean(path) &&
      (path.startsWith('/accessCredentials') ||
        path.startsWith('/pictureDayId') ||
        path.startsWith('/studentId') ||
        path.startsWith('/saved-cart'))
    );
  };

  this.isPictureDaySmartUrl = function () {
    return Boolean(this.redirectedSmartUrlRoute?.path.startsWith('/pictureDayId/'));
  };

  const SKU_SET_TRANSLATOR = {
    PACKAGE: {
      path: '/package/',
      findProduct: (skuCode) => ProductCatalogStore.findPackageBySku(skuCode)
    },
    ADDON: {
      path: '/addon/',
      findProduct: (skuCode) => ProductCatalogStore.findAddOnBySku(skuCode)
    }
  };

  this.translateSkuSetRoute = function () {
    const setName = this.redirectedSmartUrlRoute?.routeParams.setName;

    const skuSet = ContentStore.getSkuSet(setName);

    if (!skuSet) {
      return;
    }

    const { type, skus } = skuSet;

    const translator = SKU_SET_TRANSLATOR[type];

    if (!translator) {
      ErrorLogService(new Error(`Unexpected skuSet type: ${type}`));

      return;
    }

    const translatedSku = skus.find((sku) => translator.findProduct(sku)) || -1;

    this.redirectedSmartUrlRoute.path = this.redirectedSmartUrlRoute.path.replace(
      SKU_SET_REGEX,
      `${translator.path}${translatedSku}`
    );
    this.redirectedSmartUrlRoute.routeParams.setName = null;
    this.redirectedSmartUrlRoute.routeParams.sku = translatedSku;
  };

  DeserializeAction.register(this.onDeserialized.bind(this));

  SessionRecreatedAction.register(
    function () {
      if (UserProfileStore.isAuthenticated()) {
        $location.url('/my-dashboard?message=MY_DASHBOARD_SESSION_TIMEOUT');
      } else {
        $location.url('/?message=SESSION_TIMEOUT');
      }
    }.bind(this)
  );
}

App.service('RouterService', [
  'JobStore',
  'UserProfileStore',
  'ShoppingCartStore',
  'ContentStore',
  'StorageService',
  'Config',
  '$route',
  '$location',
  '$injector',
  '$rootScope',
  '$document',
  '$window',
  'DeserializeAction',
  'SessionRecreatedAction',
  'ProductCatalogStore',
  'IdentityService',
  'SessionTimeoutService',
  'ErrorLogService',
  RouterService
]);
